import type { DiscountCodeRecord } from '../services/discountCodesService'

function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${currency} ${amount.toFixed(2)}`
  }
}

export function formatDiscountBenefit(
  record: Pick<DiscountCodeRecord, 'discountType' | 'discountValue' | 'maxDiscountAmount'>,
  currency: string,
): string {
  if (record.discountType === 'percent') {
    let s = `${record.discountValue}% off`
    if (record.maxDiscountAmount != null && record.maxDiscountAmount > 0) {
      s += ` (max ${formatMoney(record.maxDiscountAmount, currency)} off)`
    }
    return s
  }
  return `${formatMoney(record.discountValue, currency)} off`
}

export function buildShortCampaignMessage(input: {
  storeName: string
  storefrontUrl: string | null
  record: DiscountCodeRecord
  currency: string
}): string {
  const benefit = formatDiscountBenefit(input.record, input.currency)
  const url = input.storefrontUrl ?? '(set VITE_PUBLIC_STOREFRONT_URL)'
  return `${input.storeName}: Use code ${input.record.code} for ${benefit} — ${url}`
}

export function buildLongCampaignMessage(input: {
  storeName: string
  storefrontUrl: string | null
  record: DiscountCodeRecord
  currency: string
}): string {
  const benefit = formatDiscountBenefit(input.record, input.currency)
  const url = input.storefrontUrl ?? '(set VITE_PUBLIC_STOREFRONT_URL)'
  let text = `Shop at ${input.storeName}! Visit ${url} and enter code ${input.record.code} at checkout to get ${benefit}.`
  if (input.record.minOrderSubtotal != null && input.record.minOrderSubtotal > 0) {
    text += ` Valid on orders over ${formatMoney(input.record.minOrderSubtotal, input.currency)}.`
  }
  if (input.record.endsAt) {
    try {
      const end = new Date(input.record.endsAt).toLocaleString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
      text += ` Offer ends ${end}.`
    } catch {
      /* skip */
    }
  }
  return text
}

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}
